import type { FeatureCode, PlanId } from '@/shared/format/plan';
import { scopedRoutes, type ScopedRouteKey } from '@/app/routes';

export interface NavLink {
  kind: 'link';
  /** Stable route id (matches `<meta ts-route>` from old shell.js). */
  routeId: string;
  /** scopedRoutes-ийн key — `Sidebar`-аас scope-той хослуулж URL build хийнэ. */
  routeKey: ScopedRouteKey;
  icon: string;
  label: string;
  accent?: boolean;
  requiredFeature?: FeatureCode;
  /** Зөвхөн тухайн BusinessType (enum int) үед харагдана. undefined бол бүгдэд. */
  requiredBusinessType?: number;
  /** Энэ багц (болон дээш) шаардлагатай. Доош багцад түгжээтэй + upgrade prompt. */
  requiredPlan?: PlanId;
}

/** Цэсний бүлэг — collapsible accordion (expanded) / icon-rail flyout (collapsed). */
export interface NavGroup {
  /** Тогтвортой id — нээлттэй/хаалттай төлвийг localStorage-д хадгалахад. */
  id: string;
  label: string;
  /** Бүлгийн icon (collapsed rail-д харагдана). */
  icon: string;
  links: NavLink[];
  /** True үед бүлгийн толгойгүй, ганц link-ийг top-level item болгож шууд render хийнэ. */
  standalone?: boolean;
}

export const NAV: NavGroup[] = [
  {
    id: 'main',
    label: 'Хянах самбар',
    icon: 'speedometer2',
    standalone: true,
    links: [
      { kind: 'link', routeId: 'dashboard', routeKey: 'dashboard', icon: 'speedometer2', label: 'Хянах самбар' },
    ],
  },
  {
    id: 'registry',
    label: 'Бүртгэл',
    icon: 'box-seam',
    links: [
      { kind: 'link', routeId: 'room', routeKey: 'rooms', icon: 'door-open', label: 'Өрөө', requiredBusinessType: 71 },
      { kind: 'link', routeId: 'item', routeKey: 'items', icon: 'box-seam', label: 'Бараа' },
      { kind: 'link', routeId: 'price-groups', routeKey: 'priceGroups', icon: 'tags', label: 'Борлуулах үнийн бүлэг', requiredPlan: 'gold' },
      { kind: 'link', routeId: 'bank-accounts', routeKey: 'bankAccounts', icon: 'bank', label: 'Банкны данс', requiredPlan: 'gold' },
      { kind: 'link', routeId: 'count', routeKey: 'counts', icon: 'clipboard2-check', label: 'Бараа материалын тооллого', requiredFeature: 'inventory.stockcount' },
      { kind: 'link', routeId: 'in-return', routeKey: 'inReturns', icon: 'arrow-return-left', label: 'Буцаалт' },
      { kind: 'link', routeId: 'transfer', routeKey: 'transfers', icon: 'arrow-left-right', label: 'Агуулахын хөдөлгөөн', requiredPlan: 'gold' },
    ],
  },
  {
    id: 'purchase',
    label: 'Худалдан авалт',
    icon: 'cart3',
    links: [
      { kind: 'link', routeId: 'vendor', routeKey: 'vendors', icon: 'truck', label: 'Нийлүүлэгч' },
      { kind: 'link', routeId: 'purchase', routeKey: 'purchases', icon: 'bag-plus', label: 'Худалдан авалт' },
      { kind: 'link', routeId: 'purchase-return', routeKey: 'purchaseReturns', icon: 'arrow-counterclockwise', label: 'Худалдан авалтын буцаалт' },
      { kind: 'link', routeId: 'ap', routeKey: 'ap', icon: 'arrow-up-right-circle', label: 'Өглөг', requiredFeature: 'ap' },
    ],
  },
  {
    id: 'sales',
    label: 'Борлуулалт',
    icon: 'graph-up-arrow',
    links: [
      { kind: 'link', routeId: 'customer', routeKey: 'customers', icon: 'person-lines-fill', label: 'Харилцагч' },
      { kind: 'link', routeId: 'salesorder', routeKey: 'sales', icon: 'receipt', label: 'Борлуулалт' },
      { kind: 'link', routeId: 'catalog', routeKey: 'catalogs', icon: 'shop', label: 'Каталог', requiredPlan: 'gold' },
      { kind: 'link', routeId: 'catalog-orders', routeKey: 'catalogOrders', icon: 'inbox', label: 'Каталогийн захиалга', requiredPlan: 'gold' },
      { kind: 'link', routeId: 'shop-store', routeKey: 'shopStore', icon: 'shop-window', label: 'Онлайн дэлгүүр', requiredPlan: 'gold' },
      { kind: 'link', routeId: 'sales-return', routeKey: 'salesReturns', icon: 'arrow-counterclockwise', label: 'Борлуулалтын буцаалт' },
      { kind: 'link', routeId: 'ar', routeKey: 'ar', icon: 'arrow-down-left-circle', label: 'Авлага', requiredFeature: 'ar' },
      { kind: 'link', routeId: 'pos', routeKey: 'pos', icon: 'display', label: 'ПОС', accent: true },
      { kind: 'link', routeId: 'pos-shifts', routeKey: 'posShifts', icon: 'clock-history', label: 'ПОС ээлжүүд' },
      { kind: 'link', routeId: 'sales-summary', routeKey: 'salesSummary', icon: 'bar-chart-line', label: 'Борлуулалтын самбар' },
    ],
  },
  {
    id: 'finance',
    label: 'Санхүү',
    icon: 'bank',
    links: [
      { kind: 'link', routeId: 'chart-of-accounts', routeKey: 'chartOfAccounts', icon: 'journal-text', label: 'Дансны төлөвлөгөө', requiredPlan: 'gold' },
      { kind: 'link', routeId: 'gl-journals', routeKey: 'glJournals', icon: 'card-list', label: 'Журнал', requiredPlan: 'gold' },
      { kind: 'link', routeId: 'fiscal-periods', routeKey: 'fiscalPeriods', icon: 'calendar3', label: 'Санхүүгийн хугацаа', requiredPlan: 'gold' },
    ],
  },
  {
    id: 'reports',
    label: 'Тайлан',
    icon: 'bar-chart-line',
    links: [
      { kind: 'link', routeId: 'report-qty', routeKey: 'reportItemQty', icon: 'bar-chart-line', label: 'Барааны үлдэгдэл' },
      // G16 — дутуу байсан тайлангууд.
      { kind: 'link', routeId: 'report-profit', routeKey: 'reportProfit', icon: 'graph-up-arrow', label: 'Ашгийн тайлан' },
      { kind: 'link', routeId: 'report-purchase', routeKey: 'reportPurchase', icon: 'bag-check', label: 'Худалдан авалтын тайлан' },
      { kind: 'link', routeId: 'report-aging', routeKey: 'reportAging', icon: 'hourglass-split', label: 'Авлага/өглөгийн насжилт' },
      { kind: 'link', routeId: 'report-txn', routeKey: 'reportTxHistory', icon: 'clock-history', label: 'Гүйлгээний түүх', requiredFeature: 'report.full' },
      { kind: 'link', routeId: 'sales-history', routeKey: 'salesHistory', icon: 'clock-history', label: 'ПОС борлуулалт' },
    ],
  },
];

/** Бүх nav link-ийг flat жагсаалт болгоно (хайлт / command palette-д). */
export function flatNavLinks(): NavLink[] {
  return NAV.flatMap((g) => g.links);
}

/** routeKey → page title (Mongolian). */
export const PAGE_TITLES: Partial<Record<ScopedRouteKey, string>> = {
  dashboard: 'Хянах самбар',
  rooms: 'Өрөө',
  items: 'Бараа',
  catalogs: 'Каталог',
  catalogEdit: 'Каталог',
  catalogOrders: 'Каталогийн захиалга',
  shopStore: 'Онлайн дэлгүүр',
  customers: 'Харилцагч',
  vendors: 'Нийлүүлэгч',
  priceGroups: 'Борлуулах үнийн бүлэг',
  bankAccounts: 'Банкны данс',
  salesHistory: 'ПОС борлуулалт',
  salesSummary: 'Борлуулалтын самбар',
  reportProfit: 'Ашгийн тайлан',
  reportPurchase: 'Худалдан авалтын тайлан',
  reportAging: 'Авлага/өглөгийн насжилт',
  purchases: 'Худалдан авалт',
  purchaseEdit: 'Худалдан авалт',
  purchaseReturns: 'Худалдан авалтын буцаалт',
  purchaseReturnEdit: 'Худалдан авалтын буцаалт',
  sales: 'Борлуулалт',
  saleEdit: 'Борлуулалт',
  salesReturns: 'Борлуулалтын буцаалт',
  salesReturnEdit: 'Борлуулалтын буцаалт',
  ar: 'Авлага',
  arEdit: 'Авлага',
  ap: 'Өглөг',
  apEdit: 'Өглөг',
  reportItemQty: 'Барааны үлдэгдэл',
  reportTxHistory: 'Гүйлгээний түүх',
  chartOfAccounts: 'Дансны төлөвлөгөө',
  glJournals: 'Журнал',
  fiscalPeriods: 'Санхүүгийн хугацаа',
  counts: 'Бараа материалын тооллого',
  countEdit: 'Бараа материалын тооллого',
  inReturns: 'Буцаалт',
  inReturnEdit: 'Буцаалт',
  transfers: 'Агуулахын хөдөлгөөн',
  transferEdit: 'Агуулахын хөдөлгөөн',
  pos: 'ПОС',
  posShifts: 'ПОС ээлжүүд',
  profile: 'Хэрэглэгчийн профайл',
  invoices: 'Багцын нэхэмжлэх',
  invoiceDetail: 'Багцын нэхэмжлэх',
  terminals: 'ПОС терминалууд',
  tickets: 'Миний ticket',
  roadmap: 'Бүтээгдэхүүний замнал',
  companySettings: 'Байгууллагын тохиргоо',
};

/**
 * Pathname → page title. URL-ийн хамгийн сүүлийн segment-ийг (sub-route) уншиж
 * `scopedRoutes` map-аас reverse-lookup хийнэ.
 *   /<tid>/<cid>/dashboard           → "Хянах самбар"
 *   /<tid>/<cid>/sale-edit?id=…      → "Борлуулалт"
 */
export function pageTitleFor(pathname: string): string {
  return breadcrumbFor(pathname).title;
}

export interface Breadcrumb {
  /** Хуудасны гарчиг ("Бараа"). */
  title: string;
  /** Тухайн хуудсыг агуулах цэсний бүлэг ("Бүртгэл"); standalone / олдоогүй бол null. */
  group: NavGroup | null;
  /** Хуудасны icon (bootstrap-icons нэр) — NAV-аас, олдохгүй бол null. */
  icon: string | null;
  routeKey: ScopedRouteKey | null;
}

/** Хуудасны гарчиг + бүлэг + icon — header breadcrumb-д. `sale-edit` гэх мэт
 *  дэд хуудас нь эх жагсаалтын (`sales`) бүлэгт харьяалагдана. */
export function breadcrumbFor(pathname: string): Breadcrumb {
  const none: Breadcrumb = { title: '', group: null, icon: null, routeKey: null };
  const segs = (pathname.split('?')[0] ?? '').split('/').filter(Boolean);
  // /:tenantId/:companyId/<page>...
  const sub = segs[2];
  if (!sub) return none;
  const entry = (Object.entries(scopedRoutes) as [ScopedRouteKey, string][]).find(
    ([, v]) => v === sub,
  );
  if (!entry) return none;
  const routeKey = entry[0];
  const title = PAGE_TITLES[routeKey] ?? '';
  // Шууд тохирох link, эсвэл ижил гарчигтай link (edit → list).
  let group: NavGroup | null = null;
  let icon: string | null = null;
  for (const g of NAV) {
    const l =
      g.links.find((x) => x.routeKey === routeKey) ??
      g.links.find((x) => PAGE_TITLES[x.routeKey] === title);
    if (l) {
      group = g.standalone ? null : g;
      icon = l.icon;
      break;
    }
  }
  return { title, group, icon, routeKey };
}
